import type { Fragment, BuildContext } from "../types.js";
import { ci, activeColorTheme } from "../../brand/theme.js";
import { withAlpha } from "../../brand/tokens.js";
import { rng } from "../util/rng.js";
import { tl } from "../timeline.js";

export type BackdropVariant =
  | "solid"
  | "gradient"
  | "spotlight"
  | "grid"
  | "noise"
  | "split"
  | "cinematic"
  | "studio"
  | "neon"
  | "allyver";

/**
 * BrandBackdrop — single canvas-filling background with 10 variants. Animations
 * use the full composition duration with `ease: "none"` so timing matches the
 * Remotion frame-based linear interpolations.
 */
export function brandBackdrop(
  ctx: BuildContext,
  id: string,
  opts: { variant?: BackdropVariant; accent?: string; animated?: boolean } = {},
): Fragment {
  const variant = opts.variant ?? "gradient";
  const accent = opts.accent ?? ci.colors.primary;
  const animated = opts.animated ?? true;
  const dur = ctx.durationSec;

  switch (variant) {
    case "solid":
      return solid(id);
    case "split":
      return split(id, accent);
    case "spotlight":
      return spotlight(id, accent, dur, animated);
    case "grid":
      return grid(id);
    case "noise":
      return noise(id, accent);
    case "cinematic":
      return cinematic(id, accent, dur, animated);
    case "studio":
      return studio(id, accent);
    case "neon":
      return neon(id, accent, dur, animated);
    case "allyver":
      return allyverLook(id, accent, dur, animated);
    case "gradient":
    default:
      return gradient(id, accent, dur, animated);
  }
}

function fillStyle(extra = ""): string {
  return `position:absolute;inset:0;${extra}`;
}

/**
 * Theme-aware „Tiefen-Schatten" für Vignetten & Gradient-Enden.
 *
 * Im Dark-Theme bleibt es das gewohnte Schwarz (cineastische Tiefe). Im Light-
 * Theme würde reines `rgba(0,0,0,0.55)` als hässlicher dunkler Ring/Boden auf dem
 * Off-White wirken — deshalb nutzen wir dort einen sehr weichen Navy-Hauch
 * (Brand-`fg`), stark gedämpft. So bleibt dezente Tiefe ohne „Schmutz".
 */
function depthShade(strength: number): string {
  return activeColorTheme === "light"
    ? withAlpha("#0f172a", strength * 0.14)
    : `rgba(0,0,0,${strength.toFixed(3)})`;
}

/**
 * Theme-aware Film-Grain-Einstellung. Auf Hell sieht `overlay`-Grain
 * fleckig/dunkel aus → leiser + `soft-light` für eine feine, helle Textur.
 */
function grainSettings(): { opacity: number; blend: string } {
  return activeColorTheme === "light"
    ? { opacity: 0.04, blend: "soft-light" }
    : { opacity: 0.09, blend: "overlay" };
}

/**
 * SVG → CSS-`url(...)`. Wir URL-enkodieren den SVG-Inhalt (inkl. `<`, `>`, `"`,
 * Whitespace) und packen ihn in `url('data:image/svg+xml;utf8,…')` mit
 * EINFACHEN Quotes. So überleben die Doppelanführungszeichen des umgebenden
 * `style="…"`-Attributs sauber – andernfalls schließt der HTML-Parser das
 * `style`-Attribut beim ersten `"` im SVG, der Rest landet als sichtbarer Text
 * im Dokument (sah man früher als „opacity:0.09 mix-blend-mode:overlay" oben
 * links im Preview).
 */
function svgUrl(svg: string): string {
  const encoded = encodeURIComponent(svg.trim()).replace(/'/g, "%27");
  return `url('data:image/svg+xml;utf8,${encoded}')`;
}

function solid(id: string): Fragment {
  return {
    html: `<div id="${id}" style="${fillStyle(`background:${ci.colors.bg};`)}"></div>`,
  };
}

function split(id: string, accent: string): Fragment {
  return {
    html: `<div id="${id}" style="${fillStyle(`background:linear-gradient(90deg, ${ci.colors.bg} 0%, ${ci.colors.bg} 50%, ${withAlpha(accent, 0.18)} 50%, ${ci.colors.bg} 100%);`)}"></div>`,
  };
}

function spotlight(
  id: string,
  accent: string,
  dur: number,
  animated: boolean,
): Fragment {
  const baseGrad = `radial-gradient(ellipse at 30% 25%, ${withAlpha(accent, 0.55)} 0%, ${withAlpha(accent, 0.08)} 35%, transparent 65%)`;
  const endGrad = `radial-gradient(ellipse at 70% 55%, ${withAlpha(accent, 0.55)} 0%, ${withAlpha(accent, 0.08)} 35%, transparent 65%)`;
  const tweens = animated
    ? [
        tl.fromTo(
          `#${id}-blob`,
          { backgroundImage: baseGrad },
          { backgroundImage: endGrad, duration: dur, ease: "none" },
          0,
        ),
      ]
    : [];
  const html = `<div id="${id}" style="${fillStyle(`background:${ci.colors.bg};`)}">
  <div id="${id}-blob" style="${fillStyle(`background:${baseGrad};`)}"></div>
  <div style="${fillStyle(`background:radial-gradient(ellipse at 80% 80%, ${withAlpha(ci.colors.secondary, 0.35)} 0%, transparent 50%);`)}"></div>
</div>`;
  return { html, tweens };
}

function grid(id: string): Fragment {
  const lineColor = withAlpha(ci.colors.fg, 0.06);
  const html = `<div id="${id}" style="${fillStyle(`background:${ci.colors.bg};`)}">
  <div style="${fillStyle(`background-image:linear-gradient(${lineColor} 1px, transparent 1px), linear-gradient(90deg, ${lineColor} 1px, transparent 1px);background-size:80px 80px;opacity:0.7;`)}"></div>
  <div style="${fillStyle(`background:radial-gradient(ellipse at 50% 50%, transparent 0%, ${ci.colors.bg} 80%);`)}"></div>
</div>`;
  return { html };
}

function noise(id: string, accent: string): Fragment {
  const noiseUrl = svgUrl(
    `<svg xmlns="http://www.w3.org/2000/svg" width="220" height="220"><filter id="n"><feTurbulence baseFrequency="0.85" numOctaves="2" stitchTiles="stitch"/></filter><rect width="100%" height="100%" filter="url(%23n)" opacity="0.55"/></svg>`,
  );
  const g = grainSettings();
  const noiseOpacity = activeColorTheme === "light" ? 0.07 : 0.18;
  const html = `<div id="${id}" style="${fillStyle(`background:${ci.colors.bg};`)}">
  <div style="${fillStyle(`background:linear-gradient(135deg, ${withAlpha(accent, 0.4)} 0%, ${ci.colors.bg} 60%);`)}"></div>
  <div style="${fillStyle(`background-image:${noiseUrl};opacity:${noiseOpacity};mix-blend-mode:${g.blend};`)}"></div>
</div>`;
  return { html };
}

function cinematic(
  id: string,
  accent: string,
  dur: number,
  animated: boolean,
): Fragment {
  const grain = svgUrl(
    `<svg xmlns="http://www.w3.org/2000/svg" width="320" height="320"><filter id="n"><feTurbulence baseFrequency="1.4" numOctaves="2" stitchTiles="stitch"/></filter><rect width="100%" height="100%" filter="url(%23n)" opacity="0.95"/></svg>`,
  );
  const g = grainSettings();
  const html = `<div id="${id}" style="${fillStyle(`background:${ci.colors.bg};`)}">
  <div id="${id}-blob1" style="${fillStyle(`background:radial-gradient(60% 60% at 20% 30%, ${withAlpha(accent, 0.55)} 0%, transparent 60%);`)}"></div>
  <div id="${id}-blob2" style="${fillStyle(`background:radial-gradient(55% 55% at 75% 20%, ${withAlpha(ci.colors.secondary, 0.45)} 0%, transparent 60%);`)}"></div>
  <div id="${id}-blob3" style="${fillStyle(`background:radial-gradient(70% 70% at 50% 85%, ${withAlpha(ci.colors.accent, 0.35)} 0%, transparent 60%);`)}"></div>
  ${bokehHtml(`${id}-bokeh`, 26, accent, ci.colors.accent)}
  <div style="${fillStyle(`background-image:${grain};opacity:${g.opacity};mix-blend-mode:${g.blend};`)}"></div>
  <div style="${fillStyle(`background:radial-gradient(ellipse at 50% 50%, transparent 35%, ${depthShade(0.55)} 100%);`)}"></div>
</div>`;
  const tweens = animated
    ? [
        tl.fromTo(
          `#${id}-blob1`,
          {
            backgroundImage: `radial-gradient(60% 60% at 20% 30%, ${withAlpha(accent, 0.55)} 0%, transparent 60%)`,
          },
          {
            backgroundImage: `radial-gradient(60% 60% at 35% 40%, ${withAlpha(accent, 0.55)} 0%, transparent 60%)`,
            duration: dur,
            ease: "none",
          },
          0,
        ),
        tl.fromTo(
          `#${id}-blob2`,
          {
            backgroundImage: `radial-gradient(55% 55% at 75% 20%, ${withAlpha(ci.colors.secondary, 0.45)} 0%, transparent 60%)`,
          },
          {
            backgroundImage: `radial-gradient(55% 55% at 63% 38%, ${withAlpha(ci.colors.secondary, 0.45)} 0%, transparent 60%)`,
            duration: dur,
            ease: "none",
          },
          0,
        ),
        tl.fromTo(
          `#${id}-blob3`,
          {
            backgroundImage: `radial-gradient(70% 70% at 50% 85%, ${withAlpha(ci.colors.accent, 0.35)} 0%, transparent 60%)`,
          },
          {
            backgroundImage: `radial-gradient(70% 70% at 50% 75%, ${withAlpha(ci.colors.accent, 0.35)} 0%, transparent 60%)`,
            duration: dur,
            ease: "none",
          },
          0,
        ),
      ]
    : [];
  return { html, tweens };
}

function studio(id: string, accent: string): Fragment {
  const html = `<div id="${id}" style="${fillStyle(`background:linear-gradient(180deg, ${withAlpha(ci.colors.fg, 0.05)} 0%, ${ci.colors.bg} 35%, ${ci.colors.bg} 70%, ${depthShade(0.55)} 100%);`)}">
  <div style="${fillStyle(`background:radial-gradient(ellipse 60% 35% at 50% 0%, ${withAlpha(ci.colors.fg, 0.18)} 0%, transparent 70%);`)}"></div>
  <div style="${fillStyle(`background:radial-gradient(ellipse 70% 25% at 50% 100%, ${withAlpha(accent, 0.25)} 0%, transparent 70%);`)}"></div>
</div>`;
  return { html };
}

function neon(
  id: string,
  accent: string,
  dur: number,
  animated: boolean,
): Fragment {
  const html = `<div id="${id}" style="${fillStyle(`background:${ci.colors.bg};`)}">
  <div style="${fillStyle(`background:linear-gradient(135deg, ${withAlpha(accent, 0.25)} 0%, transparent 40%, ${withAlpha(ci.colors.secondary, 0.2)} 100%);`)}"></div>
  <div id="${id}-line" style="position:absolute;left:0;right:0;top:50%;height:2px;background:${accent};box-shadow:0 0 24px ${accent}, 0 0 80px ${withAlpha(accent, 0.6)};"></div>
  <div style="${fillStyle(`background:repeating-linear-gradient(0deg, transparent 0 3px, ${depthShade(0.05)} 3px 4px);mix-blend-mode:${grainSettings().blend};`)}"></div>
</div>`;
  const tweens = animated
    ? [
        tl.fromTo(
          `#${id}-line`,
          { top: "46%" },
          { top: "54%", duration: dur, ease: "sine.inOut" },
          0,
        ),
      ]
    : [];
  return { html, tweens };
}

function allyverLook(
  id: string,
  accent: string,
  dur: number,
  animated: boolean,
): Fragment {
  const turq = accent;
  const violet = ci.colors.secondary;
  const pink = ci.colors.accent;
  const dot = `radial-gradient(${withAlpha(ci.colors.fg, 0.06)} 1px, transparent 1px)`;
  const html = `<div id="${id}" style="${fillStyle(`background:${ci.colors.bg};`)}">
  <div style="${fillStyle(`background-image:${dot};background-size:28px 28px;opacity:0.55;`)}"></div>
  <div id="${id}-g1" style="${fillStyle(`background:radial-gradient(45% 45% at 10% 15%, ${withAlpha(turq, 0.55)} 0%, ${withAlpha(turq, 0.12)} 45%, transparent 70%);filter:blur(4px);`)}"></div>
  <div id="${id}-g2" style="${fillStyle(`background:radial-gradient(50% 50% at 88% 78%, ${withAlpha(violet, 0.5)} 0%, ${withAlpha(violet, 0.1)} 45%, transparent 70%);filter:blur(4px);`)}"></div>
  <div id="${id}-g3" style="${fillStyle(`background:radial-gradient(28% 22% at 50% 92%, ${withAlpha(pink, 0.32)} 0%, transparent 70%);filter:blur(6px);`)}"></div>
  ${bokehHtml(`${id}-bokeh`, 14, turq, violet)}
  <div style="${fillStyle(`background:radial-gradient(ellipse at 50% 50%, transparent 45%, ${depthShade(0.55)} 95%);`)}"></div>
</div>`;
  const tweens = animated
    ? [
        tl.fromTo(
          `#${id}-g1`,
          {
            backgroundImage: `radial-gradient(45% 45% at 10% 15%, ${withAlpha(turq, 0.55)} 0%, ${withAlpha(turq, 0.12)} 45%, transparent 70%)`,
          },
          {
            backgroundImage: `radial-gradient(45% 45% at 14% 18%, ${withAlpha(turq, 0.55)} 0%, ${withAlpha(turq, 0.12)} 45%, transparent 70%)`,
            duration: dur,
            ease: "sine.inOut",
          },
          0,
        ),
        tl.fromTo(
          `#${id}-g2`,
          {
            backgroundImage: `radial-gradient(50% 50% at 88% 78%, ${withAlpha(violet, 0.5)} 0%, ${withAlpha(violet, 0.1)} 45%, transparent 70%)`,
          },
          {
            backgroundImage: `radial-gradient(50% 50% at 85% 82%, ${withAlpha(violet, 0.5)} 0%, ${withAlpha(violet, 0.1)} 45%, transparent 70%)`,
            duration: dur,
            ease: "sine.inOut",
          },
          0,
        ),
      ]
    : [];
  return { html, tweens };
}

function gradient(
  id: string,
  accent: string,
  dur: number,
  animated: boolean,
): Fragment {
  const baseGrad = `linear-gradient(135deg, ${accent} 0%, ${ci.colors.secondary} 45%, ${ci.colors.bg} 100%)`;
  const endGrad = `linear-gradient(160deg, ${accent} 0%, ${ci.colors.secondary} 45%, ${ci.colors.bg} 100%)`;
  const html = `<div id="${id}" style="${fillStyle("")}">
  <div id="${id}-base" style="${fillStyle(`background:${baseGrad};`)}"></div>
  <div style="${fillStyle(`background:radial-gradient(circle at 25% 15%, ${withAlpha(ci.colors.fg, 0.12)} 0%, transparent 55%), radial-gradient(circle at 80% 90%, ${withAlpha(ci.colors.fg, 0.08)} 0%, transparent 55%);`)}"></div>
</div>`;
  const tweens = animated
    ? [
        tl.fromTo(
          `#${id}-base`,
          { backgroundImage: baseGrad },
          { backgroundImage: endGrad, duration: dur, ease: "none" },
          0,
        ),
      ]
    : [];
  return { html, tweens };
}

function bokehHtml(
  id: string,
  count: number,
  dominant: string,
  accent: string,
): string {
  const r = rng(73);
  const dots = Array.from({ length: count }).map((_, i) => ({
    id: i,
    x: r() * 100,
    y: r() * 100,
    size: 60 + r() * 220,
    hue: r() > 0.55 ? dominant : accent,
    opacity: 0.08 + r() * 0.14,
  }));
  return `<div id="${id}" style="position:absolute;inset:0;pointer-events:none;">${dots
    .map(
      (d) =>
        `<div style="position:absolute;left:${d.x.toFixed(2)}%;top:${d.y.toFixed(2)}%;width:${d.size.toFixed(0)}px;height:${d.size.toFixed(0)}px;border-radius:50%;background:radial-gradient(circle, ${withAlpha(d.hue, d.opacity)} 0%, transparent 70%);filter:blur(8px);"></div>`,
    )
    .join("")}</div>`;
}
